<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Top bar: search + save -->
    <div class="card mb-3">
      <div class="card-body py-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px">
          <!-- Search -->
          <div class="search-box" style="min-width: 280px; max-width: 360px; flex: 1">
            <div class="position-relative">
              <input
                type="text"
                class="form-control"
                v-model="searchQuery"
                placeholder="Buscar coluna por nome..."
              />
              <i class="bx bx-search-alt search-icon"></i>
            </div>
          </div>

          <!-- Save -->
          <div class="d-flex align-items-center" style="gap: 10px">
            <transition name="fade">
              <span v-if="hasChanges" class="badge badge-soft-warning font-size-12 px-3 py-2">
                <i class="bx bx-error-circle mr-1"></i>Não salvo
              </span>
            </transition>
            <button
              type="button"
              class="btn btn-success"
              :disabled="saving || !hasChanges"
              @click="saveAll"
            >
              <i class="bx mr-1" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="card">
      <div class="card-body text-center py-5">
        <div class="spinner-border text-primary mb-3" role="status"></div>
        <p class="text-muted mb-0 font-size-14">Carregando colunas disponíveis...</p>
      </div>
    </div>

    <!-- Two-panel layout -->
    <div v-else class="row">
      <!-- LEFT: Available fields by section -->
      <div class="col-lg-6">
        <div class="card mb-3">
          <div class="card-header bg-light">
            <h6 class="mb-0 font-size-14 font-weight-semibold">
              <i class="bx bx-collection mr-1"></i>Campos Disponíveis
            </h6>
          </div>
        </div>

        <div
          v-for="section in filteredCatalog"
          :key="section.category"
          class="card section-card mb-2"
        >
          <!-- Section header -->
          <div
            class="card-header section-toggle d-flex align-items-center justify-content-between"
            @click="toggleSection(section.category)"
          >
            <div class="d-flex align-items-center">
              <div class="section-icon mr-2">
                <i class="bx font-size-18" :class="sectionIcon(section.category)"></i>
              </div>
              <h6 class="mb-0 font-size-13 font-weight-semibold">{{ section.label }}</h6>
            </div>
            <div class="d-flex align-items-center">
              <span class="badge badge-soft-success font-size-11 mr-2" v-if="enabledCount(section) > 0">
                {{ enabledCount(section) }}
              </span>
              <i
                class="bx font-size-18 text-muted transition-transform"
                :class="isSectionOpen(section.category) ? 'bx-chevron-up' : 'bx-chevron-down'"
              ></i>
            </div>
          </div>

          <!-- Section body -->
          <transition name="slide">
            <div v-show="isSectionOpen(section.category)">
              <div class="card-body p-0">
                <div
                  v-for="(field, fIdx) in section.fields"
                  :key="field.key"
                  class="available-field d-flex align-items-center px-3 py-2"
                  :class="{
                    'available-field--active': field.enabled,
                    'border-top': fIdx > 0
                  }"
                >
                  <!-- Toggle -->
                  <div class="mr-2">
                    <b-form-checkbox
                      v-model="field.enabled"
                      switch
                      size="lg"
                      @change="onToggleField(field)"
                    ></b-form-checkbox>
                  </div>

                  <!-- Info -->
                  <div class="flex-grow-1" style="min-width: 0">
                    <span class="font-size-13 d-block text-truncate" :class="field.enabled ? 'text-dark font-weight-medium' : 'text-muted'">
                      {{ field.label }}
                    </span>
                    <small class="text-muted font-size-11">{{ field.key }}</small>
                  </div>

                  <!-- Source badge -->
                  <span
                    class="badge font-size-10 px-2 py-1 ml-2"
                    :class="sourceBadge(field.source)"
                  >
                    {{ sourceLabel(field.source) }}
                  </span>
                </div>

                <div v-if="section.fields.length === 0" class="text-center text-muted py-3 font-size-13">
                  Nenhuma coluna nesta seção
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- No results -->
        <div v-if="filteredCatalog.length === 0 && searchQuery" class="card">
          <div class="card-body text-center py-4">
            <i class="bx bx-search-alt font-size-24 text-muted d-block mb-2"></i>
            <p class="text-muted mb-0">Nenhuma coluna encontrada para "<strong>{{ searchQuery }}</strong>"</p>
          </div>
        </div>
      </div>

      <!-- RIGHT: Enabled fields - single sortable list -->
      <div class="col-lg-6">
        <div class="card sticky-top" style="top: 80px">
          <div class="card-header bg-light d-flex align-items-center justify-content-between">
            <h6 class="mb-0 font-size-14 font-weight-semibold">
              <i class="bx bx-sort-alt-2 mr-1"></i>Colunas do Export
            </h6>
            <span class="badge badge-soft-primary font-size-12 px-2 py-1">
              {{ enabledFields.length }} coluna{{ enabledFields.length !== 1 ? 's' : '' }}
            </span>
          </div>
          <div class="card-body p-0">
            <!-- Empty state -->
            <div v-if="enabledFields.length === 0" class="text-center py-5">
              <i class="bx bx-columns font-size-36 text-muted d-block mb-3"></i>
              <p class="text-muted mb-1 font-size-14">Nenhuma coluna habilitada</p>
              <p class="text-muted mb-0 font-size-12">Ative campos no painel ao lado</p>
            </div>

            <!-- Sortable list -->
            <draggable
              v-else
              :list="enabledFields"
              :options="{ animation: 200, handle: '.drag-handle' }"
              @change="onSortChange"
            >
              <div
                v-for="(field, idx) in enabledFields"
                :key="field.key"
                class="enabled-field d-flex align-items-center px-3 py-2"
                :class="{ 'border-top': idx > 0 }"
              >
                <!-- Order number -->
                <span class="order-number text-muted font-size-12 mr-2">{{ idx + 1 }}</span>

                <!-- Drag handle -->
                <div class="drag-handle text-muted mr-2">
                  <i class="bx bx-grid-vertical font-size-18"></i>
                </div>

                <!-- Info -->
                <div class="flex-grow-1" style="min-width: 0">
                  <span class="font-size-13 text-dark font-weight-medium d-block text-truncate">
                    {{ field.label }}
                  </span>
                </div>

                <!-- Source badge -->
                <span
                  class="badge font-size-10 px-2 py-1 ml-2"
                  :class="sourceBadge(field.source)"
                >
                  {{ sourceLabel(field.source) }}
                </span>

                <!-- Remove button -->
                <button
                  type="button"
                  class="btn btn-link btn-sm text-muted p-0 ml-2 remove-btn"
                  @click="disableField(field)"
                  title="Remover do export"
                >
                  <i class="bx bx-x font-size-18"></i>
                </button>
              </div>
            </draggable>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import draggable from 'vuedraggable'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { getAvailableColumns, syncColumns } from './services'

export default {
  components: {
    draggable,
    Layout,
    PageHeader
  },
  name: 'Configuração de Relatórios',
  page: {
    title: 'Configuração de Relatórios',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      loading: true,
      saving: false,
      searchQuery: '',
      hasChanges: false,
      catalog: [],
      enabledFields: [],
      openSections: {},
      title: 'Configuração de Relatórios',
      items: [
        { text: 'Configurações' },
        { text: 'Relatórios', active: true }
      ]
    }
  },
  computed: {
    filteredCatalog () {
      if (!this.searchQuery.trim()) return this.catalog
      const q = this.searchQuery.toLowerCase().trim()
      return this.catalog
        .map(section => ({
          ...section,
          fields: section.fields.filter(f =>
            f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
          )
        }))
        .filter(section => section.fields.length > 0)
    }
  },
  mounted () {
    this.loadCatalog()
  },
  methods: {
    async loadCatalog () {
      this.loading = true
      try {
        const res = await getAvailableColumns()
        this.catalog = res.data.data || []

        // Build enabled fields list sorted by order
        const enabled = []
        this.catalog.forEach(section => {
          this.$set(this.openSections, section.category, section.category === 'standard')
          section.fields.forEach(field => {
            if (field.enabled) {
              enabled.push(field)
            }
          })
        })

        // Sort by order from backend
        enabled.sort((a, b) => (a.order || 999) - (b.order || 999))
        this.enabledFields = enabled

        this.hasChanges = false
      } catch (err) {
        console.error(err)
        errorMessage('Falha ao carregar colunas disponíveis.')
      } finally {
        this.loading = false
      }
    },

    toggleSection (category) {
      this.$set(this.openSections, category, !this.openSections[category])
    },
    isSectionOpen (category) {
      return !!this.openSections[category]
    },

    onToggleField (field) {
      if (field.enabled) {
        // Add to end of enabled list
        this.enabledFields.push(field)
      } else {
        // Remove from enabled list
        const idx = this.enabledFields.findIndex(f => f.key === field.key)
        if (idx !== -1) {
          this.enabledFields.splice(idx, 1)
        }
      }
      this.hasChanges = true
    },

    disableField (field) {
      // Remove from enabled list
      const idx = this.enabledFields.findIndex(f => f.key === field.key)
      if (idx !== -1) {
        this.enabledFields.splice(idx, 1)
      }
      // Update the field in the catalog
      field.enabled = false
      this.hasChanges = true
    },

    onSortChange () {
      this.hasChanges = true
    },

    enabledCount (section) {
      return section.fields.filter(f => f.enabled).length
    },

    sectionIcon (category) {
      if (category === 'standard') return 'bx-user'
      if (category === 'card_fields') return 'bx-id-card'
      if (category.startsWith('form_')) return 'bx-list-ul'
      if (category.startsWith('rc_')) return 'bx-check-shield'
      return 'bx-category'
    },

    sourceBadge (source) {
      const map = {
        'standard': 'badge-soft-primary',
        'form_field': 'badge-soft-info',
        'card_field': 'badge-soft-warning',
        'registration_control': 'badge-soft-secondary'
      }
      return map[source] || 'badge-soft-secondary'
    },
    sourceLabel (source) {
      const map = {
        'standard': 'Padrão',
        'form_field': 'Formulário',
        'card_field': 'Ficha',
        'registration_control': 'Controle'
      }
      return map[source] || source
    },

    async saveAll () {
      this.saving = true
      try {
        const columns = []

        // Enabled fields get sequential order from the sorted list
        this.enabledFields.forEach((field, idx) => {
          columns.push({
            key: field.key,
            enabled: true,
            order: idx + 1
          })
        })

        // Disabled fields
        this.catalog.forEach(section => {
          section.fields.forEach(field => {
            if (!field.enabled) {
              columns.push({
                key: field.key,
                enabled: false,
                order: 999
              })
            }
          })
        })

        await syncColumns(columns)
        successMessage('Colunas de relatório salvas com sucesso!', 2500)
        this.hasChanges = false
      } catch (err) {
        console.error(err)
        errorMessage('Falha ao salvar colunas.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
/* Section card */
.section-card {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Section toggle header */
.section-toggle {
  cursor: pointer;
  user-select: none;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 10px 14px;
  transition: background-color 0.15s;
}
.section-toggle:hover {
  background-color: #fafbfc;
}

/* Section icon */
.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f1f3f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #556ee6;
}

/* Available field rows (left panel) */
.available-field {
  transition: background-color 0.15s;
  min-height: 46px;
  border-color: #f0f0f0 !important;
}
.available-field:hover {
  background-color: #fafbfc;
}
.available-field--active {
  background-color: #f6fef9;
}
.available-field--active:hover {
  background-color: #ecfdf3;
}

/* Enabled field rows (right panel) */
.enabled-field {
  transition: background-color 0.15s;
  min-height: 46px;
  border-color: #f0f0f0 !important;
}
.enabled-field:hover {
  background-color: #f8f9fa;
}

/* Order number */
.order-number {
  min-width: 22px;
  text-align: center;
  font-weight: 600;
}

/* Drag handle */
.drag-handle {
  cursor: move;
  opacity: 0.3;
  transition: opacity 0.15s;
}
.enabled-field:hover .drag-handle {
  opacity: 0.8;
}

/* Remove button */
.remove-btn {
  opacity: 0;
  transition: opacity 0.15s;
}
.enabled-field:hover .remove-btn {
  opacity: 0.7;
}
.remove-btn:hover {
  opacity: 1 !important;
  color: #dc3545 !important;
}

/* Transitions */
.transition-transform {
  transition: transform 0.2s;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.slide-enter-active, .slide-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  opacity: 0;
}

/* Fix Bootstrap Vue switch alignment */
.custom-switch {
  padding-left: 2.75rem;
}
</style>
